import { Col, Row } from 'antd';
import AddressFormatComponent from 'components/common/AddressFormatComponent';
import IconComponent from 'components/common/IconComponent';
import LinkedDocument from 'components/common/LinkedDocument';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { DataFieldProps } from 'components/InputFieldsGenerator/types';
import MapDirection from 'components/MapDirection';
import { FORMAT_DATE_TIME_US_FOR_DISPLAY } from 'constants/days';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import moment from 'moment';
import React, { useMemo } from 'react';
import { IAppointment } from 'types/appointments';
import { ITransactionType } from 'types/services';
import { ISignatory } from 'types/transaction';
import { RoleEnum } from 'types/user';

import { getPaymentText } from '../AppointmentsTabs/helpers';

const AppointmentDetail: React.FC<{
  appointment?: IAppointment;
  transactionTypesData?: ITransactionType[];
}> = ({ appointment, transactionTypesData }) => {
  const { role: userRole, ...user } = useCurrentLoggedInUser(); // test nha
  const userAddress = {
    address1: user.address1 || '',
    city: user.city || '',
    state: user.state || '',
    zip: user.zip || '',
  };
  const signatoryIds = appointment?.signatoryIds || [];
  const duration = appointment?.duration || 0;
  const description = appointment?.description;
  const address = appointment?.address;
  const date = moment(appointment?.date || '').format(FORMAT_DATE_TIME_US_FOR_DISPLAY);
  const dateTime = date?.toString() || '';

  const signatoriesData = appointment?.transaction?.signatories?.reduce((acc, signatory) => {
    acc[signatory.id] = signatory;
    return acc;
  }, {} as any);
  const renderAppointmentSignatories = () => {
    if (!signatoriesData || !signatoryIds || signatoryIds.length === 0) return <div>None</div>;
    return (
      <div>
        {signatoryIds.map((sigId: string) => {
          const signatory: ISignatory = signatoriesData[sigId];
          return (
            <div className='flex flex-col sm:flex-row mb-2 sm:mb-1 last-of-type:mb-0 flex-nowrap'>
              <div>
                {signatory.firstName} {signatory.lastName}
              </div>
              <div className='flex sm:mt-0 mt-1 mx-2'>
                <IconComponent color='#038fde' className='mr-1' name='email' />
                <a href={`mailto:${signatory.email}`}>{signatory.email}</a>
              </div>
              <div className='flex sm:mt-0 mt-1 mx-2 sm:mx-0'>
                <IconComponent color='#038fde' className='mr-1' name='phone' />
                <a href={`tel:${signatory.phone}`}>{signatory.phone}</a>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const notary = appointment?.notary;
  const appointmentField: DataFieldProps[] = useMemo(() => {
    const appointmentFiles = appointment?.files;
    const linkedDocumentFields =
      userRole === RoleEnum.NOTARY && !notary
        ? []
        : [
            {
              label: 'Linked Documents',
              value: (
                <div>
                  {(appointmentFiles?.length || 0) > 0
                    ? appointmentFiles?.map((file) => (
                        <LinkedDocument
                          id={file.id}
                          name={file.name}
                          description={file.details.description}
                          key={file.id}
                        />
                      ))
                    : 'No linked documents.'}
                </div>
              ),
            },
          ];

    return [
      {
        label: 'Payment',
        value: `$${getPaymentText(appointment, transactionTypesData).notaryPrice}`,
        sideFields: [
          {
            label: 'Description',
            value: description,
            size: 10,
          },
        ],
      },
      {
        label: 'Duration',
        value: `${duration / 3600}h`,
        sideFields: [
          {
            label: 'Date & Time',
            value: dateTime.replace(' ', ' at '),
            size: 10,
          },
        ],
      },
      {
        label: 'Address',
        value: <AddressFormatComponent addressDetail={address} separateLines />,
      },
      ...linkedDocumentFields,
      { label: 'Signatories', value: renderAppointmentSignatories() },
    ];
  }, [appointment]);

  if (!appointment) return null;
  return (
    <Row className='gx-w-100 gx-m-0'>
      <Col flex={1} className='gx-p-0'>
        <InputFieldsGenerator dataFields={appointmentField} />
      </Col>
      <Col span={24} style={{ maxWidth: '100%', height: 300, background: '#e8e8e8' }} className='gx-p-0'>
        <MapDirection hideDirectionBox from={userAddress} to={address} style={{ width: '100%', height: 300 }} />
      </Col>
    </Row>
  );
};

export default AppointmentDetail;
